import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";

const API_URL = "https://wwwp.isepsantafe.net.ar/calificaciones/instancia-obtener";
const API_INSTANCIAS_ESCUELA =
  "https://wwwa.isepsantafe.net.ar/expedientes/calificaciones/devuelveInstanciasPorEscuela";

const TableroCalificacionesInstancia = ({
  instanciaId,
  instanciaSeleccionada,
  instanciasCurso = [],
  escuelaSeleccionada = "",
}) => {
  const [data, setData] = useState(null);
  const [dataCurso, setDataCurso] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCurso, setLoadingCurso] = useState(false);

  const [modoVista, setModoVista] = useState("INSTANCIA");
  const [instanciasSeleccionadas, setInstanciasSeleccionadas] = useState([]);
  const [instanciasEscuela, setInstanciasEscuela] = useState([]);
  const [cursosEscuelaSeleccionados, setCursosEscuelaSeleccionados] = useState([]);
  const [instanciasEscuelaSeleccionadas, setInstanciasEscuelaSeleccionadas] = useState([]);

  const [busqueda, setBusqueda] = useState("");
  const [division, setDivision] = useState("TODAS");
  const [materia, setMateria] = useState("TODAS");
  const [estado, setEstado] = useState("TODOS");
  const [detalleAlumnoAbierto, setDetalleAlumnoAbierto] = useState(null);

  const [notaMinimaAprobacion, setNotaMinimaAprobacion] = useState(6);
  const [notaMinimaEje, setNotaMinimaEje] = useState(4);
  const [regularCuentaComoAprobado, setRegularCuentaComoAprobado] =
    useState(true);
  const [criteriosPorInstancia, setCriteriosPorInstancia] = useState({});

  const criterioBase = {
    tipo: "PROMEDIO_Y_EJES",
    notaMinimaAprobacion: 6,
    notaMinimaEje: 4,
    regularCuentaComoAprobado: true,
  };

  const obtenerCriterioInstancia = (instanciaIdBuscada) => ({
    ...criterioBase,
    ...(criteriosPorInstancia[String(instanciaIdBuscada)] || {}),
  });

  const actualizarCriterioInstancia = (instanciaIdBuscada, campo, valor) => {
    const id = String(instanciaIdBuscada);
    setCriteriosPorInstancia((prev) => ({
      ...prev,
      [id]: {
        ...criterioBase,
        ...(prev[id] || {}),
        [campo]: valor,
      },
    }));
  };

  const escuelaActual =
    escuelaSeleccionada ||
    instanciaSeleccionada?.escuela ||
    data?.instancia?.escuela ||
    instanciasCurso?.[0]?.escuela ||
    "";

  const limpiarNombreHoja = (nombre) => {
    return String(nombre || "Hoja")
      .replace(/[\\/?*[\]:]/g, "")
      .substring(0, 31);
  };

  const normalizarNota = (nota) =>
    String(nota ?? "")
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

  const obtenerNotaNumerica = (nota) => {
    const texto = String(nota ?? "")
      .trim()
      .replace(",", ".");

    const match = texto.match(/-?\d+(\.\d+)?/);
    if (!match) return null;

    const numero = Number(match[0]);
    return Number.isFinite(numero) ? numero : null;
  };

  const esEjeAprobadoConCriterio = (nota, criterio = criterioBase) => {
    const n = normalizarNota(nota);
    if (!n) return false;
    if (["reprobado", "desaprobado", "no aprobado", "no-aprobado", "pendiente", "recupera", "recuperar", "libre", "ausente", "sin nota"].some((x) => n.includes(x))) return false;
    if (n.includes("aprobado")) return true;
    if (criterio.regularCuentaComoAprobado && n.includes("regular")) return true;
    const numero = obtenerNotaNumerica(nota);
    return numero !== null && numero >= Number(criterio.notaMinimaEje);
  };

  const esEjeAprobado = (nota) => esEjeAprobadoConCriterio(nota, {
    tipo: "PROMEDIO_Y_EJES",
    notaMinimaAprobacion,
    notaMinimaEje,
    regularCuentaComoAprobado,
  });
  const esMateriaAprobada = esEjeAprobado;

  const calcularResultadoAlumno = (materiasAlumno = [], criterio = criterioBase) => {
    const materiasConNota = materiasAlumno.filter((m) => normalizarNota(m.nota));
    if (materiasConNota.length === 0) {
      return { resultadoFinal: "SIN NOTAS", promedioFinal: null, aprobadas: 0, pendientes: 0, porcentaje: 0, ejesDesaprobados: [], motivoResultado: "Sin notas en esta instancia", tieneNotas: false };
    }

    const notasNumericas = materiasConNota.map((m) => {
      const texto = normalizarNota(m.nota);
      const numero = obtenerNotaNumerica(m.nota);
      if (numero !== null) return numero;
      if (texto.includes("aprobado")) return Number(criterio.notaMinimaAprobacion);
      if (criterio.regularCuentaComoAprobado && texto.includes("regular")) return Number(criterio.notaMinimaAprobacion);
      return null;
    }).filter((n) => n !== null);

    const promedioFinal = notasNumericas.length
      ? notasNumericas.reduce((acc, n) => acc + n, 0) / notasNumericas.length
      : null;
    const ejesDesaprobados = materiasConNota.filter((m) => !esEjeAprobadoConCriterio(m.nota, criterio));
    const aprobadas = materiasConNota.length - ejesDesaprobados.length;
    const pendientes = ejesDesaprobados.length;
    const porcentaje = materiasConNota.length ? Math.round((aprobadas / materiasConNota.length) * 100) : 0;
    const exigePromedio = criterio.tipo === "PROMEDIO" || criterio.tipo === "PROMEDIO_Y_EJES";
    const exigeEjes = criterio.tipo === "EJES" || criterio.tipo === "PROMEDIO_Y_EJES";

    if (exigePromedio && (promedioFinal === null || promedioFinal < Number(criterio.notaMinimaAprobacion))) {
      return { resultadoFinal: "NO APROBADO", promedioFinal, aprobadas, pendientes, porcentaje, ejesDesaprobados, motivoResultado: `No alcanza el promedio mínimo de ${criterio.notaMinimaAprobacion}`, tieneNotas: true };
    }
    if (exigeEjes && ejesDesaprobados.length > 0) {
      return { resultadoFinal: "NO APROBADO", promedioFinal, aprobadas, pendientes, porcentaje, ejesDesaprobados, motivoResultado: `Tiene eje/s con nota menor a ${criterio.notaMinimaEje}`, tieneNotas: true };
    }
    return { resultadoFinal: "APROBADO", promedioFinal, aprobadas, pendientes, porcentaje, ejesDesaprobados: [], motivoResultado: criterio.tipo === "PROMEDIO" ? "Aprobado por promedio" : criterio.tipo === "EJES" ? "Aprobado por mínimos de ejes" : "Aprobado por promedio y mínimos de ejes", tieneNotas: true };
  };

  const obtenerEstadoNota = (nota) => {
    const n = normalizarNota(nota);
    const numero = obtenerNotaNumerica(nota);

    if (!n) return "SIN NOTA";

    if (n.includes("libre")) return "LIBRE";
   if (n.includes("regular")) {
  return regularCuentaComoAprobado ? "✅ REGULAR/APROBADO" : "🟡 REGULAR";
}

if (n.includes("aprobado")) {
  return "✅ APROBADO";
}

    if (numero !== null) {
      return numero >= Number(notaMinimaEje)
        ? "EJE APROBADO"
        : "EJE NO APROBADO";
    }

    return esMateriaAprobada(nota) ? "APROBADO" : "NO APROBADO";
  };

  const materiasARecuperarTexto = (materias = []) => {
    return materias
      .filter((m) => !esMateriaAprobada(m.nota))
      .map((m) => m.nombreMateria || m.materia || "Sin materia")
      .filter(Boolean)
      .join(" | ");
  };

  const ajustarColumnas = (ws, datos) => {
    if (!datos || datos.length === 0) return;

    const cols = Object.keys(datos[0]).map((key) => {
      const max = Math.max(
        key.length,
        ...datos.map((row) => String(row[key] ?? "").length)
      );

      return {
        wch: Math.min(Math.max(max + 2, 12), 70),
      };
    });

    ws["!cols"] = cols;
  };

  const agregarHoja = (wb, nombre, datos) => {
    const ws = XLSX.utils.json_to_sheet(datos || []);
    ajustarColumnas(ws, datos || []);
    XLSX.utils.book_append_sheet(wb, ws, limpiarNombreHoja(nombre));
  };

  const cargarDatos = async () => {
    if (!instanciaId) return;

    try {
      setLoading(true);

      const res = await axios.post(API_URL, {
        id_instancia: instanciaId,
      });

      setData(res.data);
    } catch (error) {
      console.error("Error obteniendo instancia:", error);
      alert("No se pudo obtener la información de calificaciones");
    } finally {
      setLoading(false);
    }
  };

  const cargarCursoCompleto = async () => {
    try {
      const instanciasFiltradas = instanciasCurso.filter((i) =>
        instanciasSeleccionadas.includes(String(i.id))
      );

      if (!instanciasFiltradas.length) {
        setDataCurso([]);
        return;
      }

      setLoadingCurso(true);

      const respuestas = await Promise.all(
        instanciasFiltradas.map((instancia) =>
          axios.post(API_URL, {
            id_instancia: instancia.id,
          })
        )
      );

      const armado = respuestas.map((res, idx) => ({
        instancia: instanciasFiltradas[idx],
        data: res.data,
      }));

      setDataCurso(armado);
    } catch (error) {
      console.error("Error cargando curso completo:", error);
      alert("No se pudo cargar el curso completo");
    } finally {
      setLoadingCurso(false);
    }
  };

  const cargarInstanciasEscuela = async () => {
    if (!escuelaActual) {
      alert("No se pudo determinar la escuela para consultar.");
      return;
    }

    try {
      setLoadingCurso(true);

      const res = await axios.post(API_INSTANCIAS_ESCUELA, {
        escuela: escuelaActual,
      });
      const instancias = res.data?.instancias || [];
      setInstanciasEscuela(instancias);

      // Importante: en escuela NO seleccionamos nada por defecto.
      // Primero elegís curso/s y después elegís las instancias de cada curso.
      setCursosEscuelaSeleccionados([]);
      setInstanciasEscuelaSeleccionadas([]);
      setDataCurso([]);
    } catch (error) {
      console.error("Error cargando instancias de escuela:", error);
      alert("No se pudieron cargar las instancias de la escuela.");
    } finally {
      setLoadingCurso(false);
    }
  };

  const cargarEscuelaCompleta = async () => {
    try {
      const instanciasFiltradas = instanciasEscuela.filter((i) =>
        instanciasEscuelaSeleccionadas.includes(String(i.id))
      );

      if (!instanciasFiltradas.length) {
        setDataCurso([]);
        return;
      }

      setLoadingCurso(true);

      const respuestas = await Promise.all(
        instanciasFiltradas.map((instancia) =>
          axios.post(API_URL, {
            id_instancia: instancia.id,
          })
        )
      );

      const armado = respuestas.map((res, idx) => ({
        instancia: instanciasFiltradas[idx],
        data: res.data,
      }));

      setDataCurso(armado);
    } catch (error) {
      console.error("Error cargando escuela completa:", error);
      alert("No se pudo cargar la escuela completa.");
    } finally {
      setLoadingCurso(false);
    }
  };

  useEffect(() => {
    cargarDatos();
  }, [instanciaId]);

useEffect(() => {
  if (instanciasCurso?.length > 0) {
    setInstanciasSeleccionadas(
      instanciasCurso
        .filter(
          (i) =>
            String(i.estado || "").trim().toLowerCase() === "activo"
        )
        .map((i) => String(i.id))
    );
  }
}, [instanciasCurso]);

  useEffect(() => {
    if (modoVista === "CURSO" && instanciasSeleccionadas.length > 0) {
      cargarCursoCompleto();
    }
  }, [modoVista, instanciasSeleccionadas]);


  useEffect(() => {
    if (modoVista === "ESCUELA" && instanciasEscuela.length === 0) {
      cargarInstanciasEscuela();
    }
  }, [modoVista]);

  useEffect(() => {
    if (
      modoVista === "ESCUELA" &&
      instanciasEscuela.length > 0 &&
      instanciasEscuelaSeleccionadas.length > 0
    ) {
      cargarEscuelaCompleta();
    }

    if (modoVista === "ESCUELA" && instanciasEscuelaSeleccionadas.length === 0) {
      setDataCurso([]);
    }
  }, [modoVista, instanciasEscuela, instanciasEscuelaSeleccionadas]);

  const toggleInstanciaCurso = (id) => {
    const idString = String(id);

    setInstanciasSeleccionadas((prev) =>
      prev.includes(idString)
        ? prev.filter((x) => x !== idString)
        : [...prev, idString]
    );
  };

const seleccionarTodasInstancias = () => {
  setInstanciasSeleccionadas(
    instanciasCurso
      .filter(
        (i) =>
          String(i.estado || "").trim().toLowerCase() === "activo"
      )
      .map((i) => String(i.id))
  );
};
  const limpiarInstanciasSeleccionadas = () => {
    setInstanciasSeleccionadas([]);
    setDataCurso([]);
  };


  const getCursoIdInstancia = (instancia) =>
    String(instancia?.cursoId || instancia?.cursoID || instancia?.idCurso || "");

  const esInstanciaActiva = (instancia) =>
    String(instancia?.estado || "").trim().toLowerCase() === "activo";

  const toggleCursoEscuela = (cursoId) => {
    const idString = String(cursoId);

    setCursosEscuelaSeleccionados((prev) => {
      const estabaSeleccionado = prev.includes(idString);

      if (estabaSeleccionado) {
        // Si saco un curso, también saco sus instancias del cálculo.
        const idsInstanciasDelCurso = instanciasEscuela
          .filter((i) => getCursoIdInstancia(i) === idString)
          .map((i) => String(i.id));

        setInstanciasEscuelaSeleccionadas((prevInstancias) =>
          prevInstancias.filter((id) => !idsInstanciasDelCurso.includes(id))
        );

        return prev.filter((x) => x !== idString);
      }

      return [...prev, idString];
    });
  };

  const toggleInstanciaEscuela = (instanciaId) => {
    const idString = String(instanciaId);

    setInstanciasEscuelaSeleccionadas((prev) =>
      prev.includes(idString)
        ? prev.filter((x) => x !== idString)
        : [...prev, idString]
    );
  };

  const seleccionarTodasInstanciasCursoEscuela = (cursoId) => {
    const idString = String(cursoId);

    const idsInstanciasDelCurso = instanciasEscuela
      .filter((i) => getCursoIdInstancia(i) === idString && esInstanciaActiva(i))
      .map((i) => String(i.id));

    setInstanciasEscuelaSeleccionadas((prev) => [
      ...new Set([...prev, ...idsInstanciasDelCurso]),
    ]);
  };

  const limpiarInstanciasCursoEscuela = (cursoId) => {
    const idString = String(cursoId);

    const idsInstanciasDelCurso = instanciasEscuela
      .filter((i) => getCursoIdInstancia(i) === idString)
      .map((i) => String(i.id));

    setInstanciasEscuelaSeleccionadas((prev) =>
      prev.filter((id) => !idsInstanciasDelCurso.includes(id))
    );
  };

  const seleccionarTodosCursosEscuela = () => {
    setCursosEscuelaSeleccionados(cursosEscuela.map((c) => String(c.id)));
    // Sólo abre/despliega los cursos. Las instancias se siguen eligiendo aparte.
    setInstanciasEscuelaSeleccionadas([]);
    setDataCurso([]);
  };

  const limpiarCursosEscuela = () => {
    setCursosEscuelaSeleccionados([]);
    setInstanciasEscuelaSeleccionadas([]);
    setDataCurso([]);
  };

  const normalizarAlumnosInstancia = (datos, instanciaExtra = null) => {
    if (!datos?.alumnos) return [];

    return datos.alumnos.map((alumno) => {
      const materiasAlumno = alumno.materias || [];
      const totalMaterias = materiasAlumno.length;
      const instanciaIdNormalizada = instanciaExtra?.id || datos.instancia?.id;
      const criterioAplicado = instanciaExtra
        ? obtenerCriterioInstancia(instanciaIdNormalizada)
        : { tipo: "PROMEDIO_Y_EJES", notaMinimaAprobacion, notaMinimaEje, regularCuentaComoAprobado };
      const resultado = calcularResultadoAlumno(materiasAlumno, criterioAplicado);

      return {
        ...alumno,
        instanciaId: instanciaIdNormalizada,
        nombreInstancia:
          instanciaExtra?.nombreInstancia ||
          datos.instancia?.nombreInstancia ||
          "",
        nombreCurso:
          instanciaExtra?.nombreCurso || datos.instancia?.nombreCurso || "",
        nombreCompleto: `${alumno.apellido || ""}, ${
          alumno.nombres || ""
        }`.trim(),
        materias: materiasAlumno,
        totalMaterias,
        aprobadas: resultado.aprobadas,
        pendientes: resultado.pendientes,
        porcentaje: resultado.porcentaje,
        promedioFinal: resultado.promedioFinal,
        resultadoFinal: resultado.resultadoFinal,
        motivoResultado: resultado.motivoResultado,
        ejesDesaprobados: resultado.ejesDesaprobados,
        tieneNotas: resultado.tieneNotas,
        criterioAplicado,
      };
    });
  };

  const alumnosNormalizados = useMemo(() => {
    return normalizarAlumnosInstancia(data);
  }, [
    data,
    notaMinimaAprobacion,
    notaMinimaEje,
    regularCuentaComoAprobado,
  ]);

  const alumnosCursoDetalle = useMemo(() => {
    return dataCurso.flatMap((item) =>
      normalizarAlumnosInstancia(item.data, item.instancia)
    );
  }, [
    dataCurso,
    notaMinimaAprobacion,
    notaMinimaEje,
    regularCuentaComoAprobado,
    criteriosPorInstancia,
  ]);

  const ordenMeritoCurso = useMemo(() => {
    const mapa = {};

    alumnosCursoDetalle.forEach((a) => {
      const dni = String(a.dni);

      if (!mapa[dni]) {
        mapa[dni] = {
          dni: a.dni,
          nombreCompleto: a.nombreCompleto,
          division: a.division,
          instancias: new Set(),
          totalMaterias: 0,
          aprobadas: 0,
          pendientes: 0,
          noAprobadoInstancias: 0,
          sinDefinirInstancias: 0,
          aprobadoInstancias: 0,
          sumaPromedios: 0,
          cantidadPromedios: 0,
          instanciasConNotas: 0,
          materiasDetalle: [],
          instanciasDetalle: [],
        };
      }

      mapa[dni].instancias.add(a.nombreInstancia);
      mapa[dni].instanciasDetalle.push({
        instanciaId: String(a.instanciaId || ""),
        nombreInstancia: a.nombreInstancia || "Sin instancia",
        nombreCurso: a.nombreCurso || "",
        promedio: a.promedioFinal,
        resultado: a.resultadoFinal,
        materias: a.materias || [],
        criterio: a.criterioAplicado,
        tieneNotas: a.tieneNotas,
      });
      mapa[dni].totalMaterias += a.totalMaterias;
      mapa[dni].aprobadas += a.aprobadas;
      mapa[dni].pendientes += a.pendientes;

      if (a.tieneNotas && a.promedioFinal !== null) {
        mapa[dni].sumaPromedios += a.promedioFinal;
        mapa[dni].cantidadPromedios += 1;
        mapa[dni].instanciasConNotas += 1;
      }

      if (a.resultadoFinal === "APROBADO") mapa[dni].aprobadoInstancias += 1;
      if (a.resultadoFinal === "NO APROBADO")
        mapa[dni].noAprobadoInstancias += 1;
      if (a.resultadoFinal === "SIN DEFINIR")
        mapa[dni].sinDefinirInstancias += 1;

      a.materias.forEach((m) => {
        mapa[dni].materiasDetalle.push({
          curso: a.nombreCurso || "",
          instanciaId: String(a.instanciaId || ""),
          instancia: a.nombreInstancia,
          materia: m.nombreMateria || "Sin materia",
          nombreMateria: m.nombreMateria || "Sin materia",
          nota: m.nota || "",
          estadoNota: obtenerEstadoNota(m.nota),
          califico: m.califico || "",
        });
      });
    });

    return Object.values(mapa)
      .map((a) => {
        const porcentaje =
          a.totalMaterias > 0
            ? Math.round((a.aprobadas / a.totalMaterias) * 100)
            : 0;

        const promedioFinal =
          a.cantidadPromedios > 0
            ? a.sumaPromedios / a.cantidadPromedios
            : 0;

        const instanciasSeleccionadasActuales = dataCurso.map((x) => ({
          id: String(x.instancia?.id || ""),
          nombre: x.instancia?.nombreInstancia || "Sin instancia",
        }));
        const presentes = new Set(a.instanciasDetalle.filter((d) => d.tieneNotas).map((d) => String(d.instanciaId)));
        const faltantes = instanciasSeleccionadasActuales.filter((i) => !presentes.has(i.id));
        return {
          ...a,
          cantidadInstancias: instanciasSeleccionadasActuales.length,
          instanciasConNotas: presentes.size,
          instanciasSinNotas: faltantes.length,
          instanciasFaltantesTexto: faltantes.map((i) => i.nombre).join(" | "),
          advertencia: faltantes.length ? `Tiene notas en ${presentes.size} de ${instanciasSeleccionadasActuales.length} instancia/s. Revisar si corresponde: ${faltantes.map((i) => i.nombre).join(" | ")}` : "Completo",
          instanciasTexto: [...a.instancias].join(" | "),
          porcentaje,
          promedioFinal,
          estadoGeneral: a.noAprobadoInstancias === 0 && a.pendientes === 0 ? (faltantes.length ? "APROBADO CON ADVERTENCIA" : "APROBADO") : "A REVISAR",
        };
      })
.sort((a, b) => {
  // 1. Primero los que recuperan menos materias:
  // 0 pendientes, luego 1, luego 2, luego 3, etc.
  if (a.pendientes !== b.pendientes) {
    return a.pendientes - b.pendientes;
  }

  // 2. Dentro de cada grupo, promedio de mayor a menor.
  if (b.promedioFinal !== a.promedioFinal) {
    return b.promedioFinal - a.promedioFinal;
  }

  // 3. Ante igualdad de promedio, mayor porcentaje aprobado.
  if (b.porcentaje !== a.porcentaje) {
    return b.porcentaje - a.porcentaje;
  }

  // 4. Luego quien tenga más materias/ejes aprobados.
  if (b.aprobadas !== a.aprobadas) {
    return b.aprobadas - a.aprobadas;
  }

  // 5. Luego quien tenga menos instancias no aprobadas.
  if (a.noAprobadoInstancias !== b.noAprobadoInstancias) {
    return a.noAprobadoInstancias - b.noAprobadoInstancias;
  }

  // 6. Desempate final alfabético.
  return String(a.nombreCompleto || "").localeCompare(
    String(b.nombreCompleto || ""),
    "es",
    { sensitivity: "base" }
  );
})
.map((a, idx) => ({
  ...a,
  ordenMerito: idx + 1,
}))
  }, [
    alumnosCursoDetalle,
    notaMinimaAprobacion,
    notaMinimaEje,
    regularCuentaComoAprobado,
    criteriosPorInstancia,
    dataCurso,
  ]);

  const materias = useMemo(() => {
    const set = new Set();

    alumnosNormalizados.forEach((a) => {
      a.materias.forEach((m) => set.add(m.nombreMateria));
    });

    return [...set].filter(Boolean).sort();
  }, [alumnosNormalizados]);

  const divisiones = useMemo(() => {
    return [...new Set(alumnosNormalizados.map((a) => a.division))]
      .filter(Boolean)
      .sort((a, b) => Number(a) - Number(b));
  }, [alumnosNormalizados]);

  const divisionesCurso = useMemo(() => {
    return [...new Set(ordenMeritoCurso.map((a) => a.division))]
      .filter(Boolean)
      .sort((a, b) => Number(a) - Number(b));
  }, [ordenMeritoCurso]);

  const alumnosFiltrados = useMemo(() => {
    const q = busqueda.toLowerCase().trim();

    return alumnosNormalizados.filter((alumno) => {
      const coincideBusqueda =
        !q ||
        String(alumno.dni).includes(q) ||
        alumno.nombreCompleto.toLowerCase().includes(q);

      const coincideDivision =
        division === "TODAS" || String(alumno.division) === String(division);

      const coincideEstado =
        estado === "TODOS" || alumno.resultadoFinal === estado;

      const coincideMateria =
        materia === "TODAS" ||
        alumno.materias.some((m) => m.nombreMateria === materia);

      return (
        coincideBusqueda &&
        coincideDivision &&
        coincideEstado &&
        coincideMateria
      );
    });
  }, [alumnosNormalizados, busqueda, division, estado, materia]);

  const ordenMeritoFiltrado = useMemo(() => {
    const q = busqueda.toLowerCase().trim();

    return ordenMeritoCurso.filter((alumno) => {
      const coincideBusqueda =
        !q ||
        String(alumno.dni).includes(q) ||
        alumno.nombreCompleto.toLowerCase().includes(q);

      const coincideDivision =
        division === "TODAS" || String(alumno.division) === String(division);

      const coincideEstado =
        estado === "TODOS" ||
        alumno.estadoGeneral === estado ||
        (estado === "APROBADO" && alumno.estadoGeneral === "APROBADO") ||
        (estado === "NO APROBADO" && alumno.estadoGeneral === "A REVISAR");

      return coincideBusqueda && coincideDivision && coincideEstado;
    });
  }, [ordenMeritoCurso, busqueda, division, estado]);

  const resumenMaterias = useMemo(() => {
    const mapa = {};

    alumnosNormalizados.forEach((alumno) => {
      alumno.materias.forEach((m) => {
        const nombre = m.nombreMateria || "Sin materia";

        if (!mapa[nombre]) {
          mapa[nombre] = {
            materia: nombre,
            total: 0,
            aprobados: 0,
            noAprobados: 0,
            regulares: 0,
            libres: 0,
            docentes: new Set(),
          };
        }

        mapa[nombre].total += 1;

        const notaNormalizada = normalizarNota(m.nota);

        if (notaNormalizada.includes("regular")) mapa[nombre].regulares += 1;
        if (notaNormalizada.includes("libre")) mapa[nombre].libres += 1;

        if (esMateriaAprobada(m.nota)) {
          mapa[nombre].aprobados += 1;
        } else {
          mapa[nombre].noAprobados += 1;
        }

        if (m.califico) mapa[nombre].docentes.add(m.califico);
      });
    });

    return Object.values(mapa)
      .map((x) => ({
        ...x,
        porcentaje:
          x.total > 0 ? Math.round((x.aprobados / x.total) * 100) : 0,
        docentes: [...x.docentes],
      }))
      .sort((a, b) => a.materia.localeCompare(b.materia));
  }, [
    alumnosNormalizados,
    notaMinimaAprobacion,
    notaMinimaEje,
    regularCuentaComoAprobado,
  ]);

  const resumenDivisiones = useMemo(() => {
    const mapa = {};

    alumnosNormalizados.forEach((a) => {
      const div = a.division || "Sin división";

      if (!mapa[div]) {
        mapa[div] = {
          division: div,
          total: 0,
          aprobados: 0,
          noAprobados: 0,
        };
      }

      mapa[div].total += 1;

      if (a.resultadoFinal === "APROBADO") {
        mapa[div].aprobados += 1;
      } else {
        mapa[div].noAprobados += 1;
      }
    });

    return Object.values(mapa)
      .map((d) => ({
        ...d,
        porcentaje:
          d.total > 0 ? Math.round((d.aprobados / d.total) * 100) : 0,
      }))
      .sort((a, b) => Number(a.division) - Number(b.division));
  }, [alumnosNormalizados]);

  const resumenCurso = useMemo(() => {
    const totalAlumnos = ordenMeritoCurso.length;

    const aprobados = ordenMeritoCurso.filter(
      (a) => a.estadoGeneral === "APROBADO"
    ).length;

    const revisar = totalAlumnos - aprobados;

    const promedio =
      totalAlumnos > 0
        ? ordenMeritoCurso.reduce((acc, a) => acc + a.promedioFinal, 0) /
          totalAlumnos
        : 0;

    return {
      totalAlumnos,
      aprobados,
      revisar,
      promedio,
      instancias: dataCurso.length,
    };
  }, [ordenMeritoCurso, dataCurso]);

  const resumenDivisionesCurso = useMemo(() => {
    const mapa = {};

    ordenMeritoCurso.forEach((a) => {
      const div = a.division || "Sin división";

      if (!mapa[div]) {
        mapa[div] = {
          division: div,
          total: 0,
          aprobados: 0,
          revisar: 0,
          promedio: 0,
          suma: 0,
        };
      }

      mapa[div].total += 1;
      mapa[div].suma += a.promedioFinal;

      if (a.estadoGeneral === "APROBADO") mapa[div].aprobados += 1;
      else mapa[div].revisar += 1;
    });

    return Object.values(mapa)
      .map((d) => ({
        ...d,
        promedio: d.total > 0 ? d.suma / d.total : 0,
      }))
      .sort((a, b) => Number(a.division) - Number(b.division));
  }, [ordenMeritoCurso]);

  const alumnosEnRiesgo = useMemo(() => {
    return alumnosNormalizados
      .filter((a) => a.resultadoFinal !== "APROBADO" || a.pendientes > 0)
      .sort((a, b) => b.pendientes - a.pendientes);
  }, [alumnosNormalizados]);

  const cursosEscuela = useMemo(() => {
    const mapa = new Map();

    instanciasEscuela.forEach((i) => {
      const id = i.cursoId || i.cursoID || i.idCurso;

      if (!id) return;

      if (!mapa.has(String(id))) {
        mapa.set(String(id), {
          id,
          nombreCurso: i.nombreCurso,
          sede: i.sede,
          escuela: i.escuela,
        });
      }
    });

    return [...mapa.values()].sort((a, b) =>
      String(a.nombreCurso || "").localeCompare(String(b.nombreCurso || ""))
    );
  }, [instanciasEscuela]);

  const instanciasEscuelaActivas = useMemo(() => {
    return instanciasEscuela.filter(esInstanciaActiva);
  }, [instanciasEscuela]);

  const instanciasEscuelaPorCurso = useMemo(() => {
    const mapa = new Map();

    instanciasEscuelaActivas.forEach((instancia) => {
      const cursoId = getCursoIdInstancia(instancia);
      if (!cursoId) return;

      if (!mapa.has(cursoId)) mapa.set(cursoId, []);
      mapa.get(cursoId).push(instancia);
    });

    mapa.forEach((lista) => {
      lista.sort((a, b) => {
        const ordenA = Number(a.orden ?? a.ordenInstancia ?? 0);
        const ordenB = Number(b.orden ?? b.ordenInstancia ?? 0);
        if (ordenA !== ordenB) return ordenA - ordenB;
        return String(a.nombreInstancia || "").localeCompare(
          String(b.nombreInstancia || "")
        );
      });
    });

    return mapa;
  }, [instanciasEscuelaActivas]);

  const cantidadInstanciasEscuelaSeleccionadasActivas = instanciasEscuelaActivas.filter(
    (i) => instanciasEscuelaSeleccionadas.includes(String(i.id))
  ).length;

  const total = alumnosNormalizados.length;

  const cantidadAprobadosCalculados = alumnosNormalizados.filter(
    (a) => a.resultadoFinal === "APROBADO"
  ).length;

  const cantidadNoAprobadosCalculados = total - cantidadAprobadosCalculados;

  const porcentajeAprobados =
    total > 0 ? Math.round((cantidadAprobadosCalculados / total) * 100) : 0;

  const formatearPromedio = (valor) => {
    const numero = Number(valor || 0);
    return numero.toFixed(2);
  };

  // Catálogo completo de instancias y ejes seleccionados.
  // Se usa para que TODOS los alumnos tengan las mismas columnas en Excel,
  // aunque alguno no posea una nota cargada.
  const catalogoInstanciasExcel = useMemo(() => {
    return dataCurso.map((item) => {
      const instancia = item.instancia || item.data?.instancia || {};
      const ejes = new Set();

      (item.data?.alumnos || []).forEach((alumno) => {
        (alumno.materias || []).forEach((materia) => {
          const nombre = materia.nombreMateria || materia.materia;
          if (nombre) ejes.add(nombre);
        });
      });

      return {
        id: String(instancia.id || item.data?.instancia?.id || ""),
        curso: instancia.nombreCurso || item.data?.instancia?.nombreCurso || "",
        instancia:
          instancia.nombreInstancia ||
          item.data?.instancia?.nombreInstancia ||
          "Sin instancia",
        ejes: [...ejes].sort((a, b) =>
          String(a).localeCompare(String(b), "es", { sensitivity: "base" })
        ),
      };
    });
  }, [dataCurso]);

  const construirColumnasPorInstanciaExcel = (alumno) => {
    const columnas = {};

    catalogoInstanciasExcel.forEach((instanciaCatalogo) => {
      const prefijo = instanciaCatalogo.curso
        ? `${instanciaCatalogo.curso} - ${instanciaCatalogo.instancia}`
        : instanciaCatalogo.instancia;

      const detalleInstancia = (alumno.instanciasDetalle || []).find(
        (detalle) => String(detalle.instanciaId) === String(instanciaCatalogo.id)
      );

      instanciaCatalogo.ejes.forEach((eje) => {
        const materiaEncontrada = detalleInstancia?.materias?.find(
          (materia) =>
            String(materia.nombreMateria || materia.materia || "") === String(eje)
        );

        const nota = materiaEncontrada?.nota;
        columnas[`${prefijo} - ${eje}`] =
          nota === null || nota === undefined || String(nota).trim() === ""
            ? "Sin nota"
            : nota;
      });

      const tieneAlgunaNota = (detalleInstancia?.materias || []).some(
        (materia) =>
          materia.nota !== null &&
          materia.nota !== undefined &&
          String(materia.nota).trim() !== ""
      );

      columnas[`${prefijo} - Promedio`] =
        detalleInstancia && tieneAlgunaNota
          ? formatearPromedio(detalleInstancia.promedio)
          : "Sin nota";

      columnas[`${prefijo} - Resultado`] =
        detalleInstancia && tieneAlgunaNota
          ? detalleInstancia.resultado || "Sin definir"
          : "Sin nota";
    });

    return columnas;
  };

  const alumnosAExcel = (lista) => {
    return lista.map((a) => {
      const fila = {
        DNI: a.dni,
        "Apellido y Nombre": a.nombreCompleto,
        División: a.division,
        Resultado: a.resultadoFinal,
        Motivo: a.motivoResultado,
        "Promedio Final": formatearPromedio(a.promedioFinal),
        "Total Materias/Ejes": a.totalMaterias,
        Aprobadas: a.aprobadas,
        Pendientes: a.pendientes,
        "Materias/Ejes a Recuperar": materiasARecuperarTexto(a.materias),
        "Porcentaje Ejes Aprobados": `${a.porcentaje}%`,
      };

      materias.forEach((mat) => {
        const encontrada = a.materias.find((m) => m.nombreMateria === mat);
        fila[mat] = encontrada?.nota || "";
        fila[`${mat} - Estado`] = encontrada
          ? obtenerEstadoNota(encontrada.nota)
          : "";
      });

      return fila;
    });
  };

  const exportarExcel = () => {
    if (!data) return;

    const wb = XLSX.utils.book_new();

    agregarHoja(wb, "Resumen Total", [
      {
        Instancia: data.instancia?.nombreInstancia || "",
        Curso: data.instancia?.nombreCurso || "",
        "Promedio mínimo aprobatorio": notaMinimaAprobacion,
        "Nota mínima por eje": notaMinimaEje,
        "Regular cuenta como aprobado": regularCuentaComoAprobado ? "Sí" : "No",
        "Total Alumnos": total,
        Aprobados: cantidadAprobadosCalculados,
        "No Aprobados": cantidadNoAprobadosCalculados,
        "% Aprobación": `${porcentajeAprobados}%`,
        "Cantidad Materias/Ejes": materias.length,
        "Alumnos para revisar": alumnosEnRiesgo.length,
      },
    ]);

    agregarHoja(
      wb,
      "Resumen Divisiones",
      resumenDivisiones.map((d) => ({
        División: d.division,
        Total: d.total,
        Aprobados: d.aprobados,
        "No Aprobados": d.noAprobados,
        "% Aprobación": `${d.porcentaje}%`,
      }))
    );

    agregarHoja(
      wb,
      "Resumen Materias",
      resumenMaterias.map((m) => ({
        Materia: m.materia,
        Total: m.total,
        Aprobados: m.aprobados,
        "No Aprobados": m.noAprobados,
        Regulares: m.regulares,
        Libres: m.libres,
        "% Aprobación": `${m.porcentaje}%`,
        Calificadores: m.docentes.join(" | "),
      }))
    );

    agregarHoja(wb, "Alumnos", alumnosAExcel(alumnosNormalizados));

    agregarHoja(
      wb,
      "Para Revisar",
      alumnosEnRiesgo.map((a) => ({
        DNI: a.dni,
        "Apellido y Nombre": a.nombreCompleto,
        División: a.division,
        Resultado: a.resultadoFinal,
        Motivo: a.motivoResultado,
        "Promedio Final": formatearPromedio(a.promedioFinal),
        Pendientes: a.pendientes,
        "Materias/Ejes a Recuperar": materiasARecuperarTexto(a.materias),
        "Total Materias/Ejes": a.totalMaterias,
        "% Ejes Aprobados": `${a.porcentaje}%`,
      }))
    );

    divisiones.forEach((div) => {
      const alumnosDivision = alumnosNormalizados.filter(
        (a) => String(a.division) === String(div)
      );

      agregarHoja(wb, `Division ${div}`, alumnosAExcel(alumnosDivision));
    });

    const nombreCurso = limpiarNombreHoja(
      data.instancia?.nombreCurso || "calificaciones"
    );

    XLSX.writeFile(
      wb,
      `calificaciones_${nombreCurso}_instancia_${instanciaId}.xlsx`
    );
  };

  const exportarCursoCompleto = async () => {
    try {
      if (!ordenMeritoCurso.length) {
        alert(
          "No hay datos del curso para exportar. Seleccioná una o más instancias."
        );
        return;
      }

      const wb = XLSX.utils.book_new();

      agregarHoja(
        wb,
        "Orden de Merito",
        ordenMeritoCurso.map((a) => ({
          Orden: a.ordenMerito,
          Curso: [
            ...new Set(a.materiasDetalle.map((m) => m.curso).filter(Boolean)),
          ].join(" | "),
          DNI: a.dni,
          "Apellido y Nombre": a.nombreCompleto,
          División: a.division,
          Estado: a.estadoGeneral,
          Instancias: a.cantidadInstancias,
          "Promedio General (solo instancias con notas)": formatearPromedio(a.promedioFinal),
          "Instancias con notas": a.instanciasConNotas,
          "Instancias sin notas": a.instanciasSinNotas,
          "Advertencia": a.advertencia,
          ...construirColumnasPorInstanciaExcel(a),
          "Total Materias/Ejes": a.totalMaterias,
          Aprobadas: a.aprobadas,
          Pendientes: a.pendientes,
          "Materias/Ejes a Recuperar": materiasARecuperarTexto(
            a.materiasDetalle
          ),
          "% Ejes Aprobados": `${a.porcentaje}%`,
          "Instancias No Aprobadas": a.noAprobadoInstancias,
          "Instancias Sin Definir": a.sinDefinirInstancias,
        }))
      );
      agregarHoja(wb, "Resumen Curso", [
        {
          Curso:
            instanciaSeleccionada?.nombreCurso ||
            instanciasCurso[0]?.nombreCurso ||
            "",
          "Criterios": "Configuración independiente por instancia",
          "Instancias Seleccionadas": resumenCurso.instancias,
          "Total Alumnos": resumenCurso.totalAlumnos,
          Aprobados: resumenCurso.aprobados,
          "A revisar": resumenCurso.revisar,
          "Promedio General": formatearPromedio(resumenCurso.promedio),
        },
      ]);

      const instanciasUsadasExcel =
        modoVista === "ESCUELA"
          ? instanciasEscuela.filter((i) =>
              instanciasEscuelaSeleccionadas.includes(String(i.id))
            )
          : instanciasCurso.filter((i) =>
              instanciasSeleccionadas.includes(String(i.id))
            );

      agregarHoja(
        wb,
        "Instancias Usadas",
        instanciasUsadasExcel.map((i) => ({
          ID: i.id,
          Instancia: i.nombreInstancia,
          Curso: i.nombreCurso,
          Estado: i.estado,
        }))
      );

      agregarHoja(
        wb,
        "Resumen Divisiones",
        resumenDivisionesCurso.map((d) => ({
          División: d.division,
          Total: d.total,
          Aprobados: d.aprobados,
          "A Revisar": d.revisar,
          "Promedio General": formatearPromedio(d.promedio),
        }))
      );

      agregarHoja(
        wb,
        "Detalle Materias",
        ordenMeritoCurso.flatMap((a) =>
          a.materiasDetalle.map((m) => ({
            Orden: a.ordenMerito,
            DNI: a.dni,
            "Apellido y Nombre": a.nombreCompleto,
            División: a.division,
            Instancia: m.instancia,
            Materia: m.materia,
            Nota:
              m.nota === null ||
              m.nota === undefined ||
              String(m.nota).trim() === ""
                ? "Sin nota"
                : m.nota,
            "Estado Nota": m.estadoNota || "SIN NOTA",
            Calificador: m.califico,
          }))
        )
      );

      agregarHoja(
        wb,
        "Para Revisar",
        ordenMeritoCurso
          .filter((a) => a.estadoGeneral !== "APROBADO")
          .map((a) => ({
            Orden: a.ordenMerito,
            DNI: a.dni,
            "Apellido y Nombre": a.nombreCompleto,
            División: a.division,
            Pendientes: a.pendientes,
            "Promedio Final": formatearPromedio(a.promedioFinal),
            "Materias/Ejes a Recuperar": materiasARecuperarTexto(
              a.materiasDetalle
            ),
            "% Ejes Aprobados": `${a.porcentaje}%`,
            "Instancias No Aprobadas": a.noAprobadoInstancias,
          }))
      );


      dataCurso.forEach((item) => {
        const instancia = item.instancia || {};
        const criterio = obtenerCriterioInstancia(instancia.id);
        const alumnosInstancia = normalizarAlumnosInstancia(item.data, instancia);
        const porDni = new Map(alumnosInstancia.map((a) => [String(a.dni), a]));
        const ejes = [...new Set(alumnosInstancia.flatMap((a) =>
          (a.materias || []).map((m) => m.nombreMateria || m.materia).filter(Boolean)
        ))];

        const filas = ordenMeritoCurso.map((general) => {
          const a = porDni.get(String(general.dni));
          const fila = {
            Orden: general.ordenMerito,
            DNI: general.dni,
            "Apellido y Nombre": general.nombreCompleto,
            División: general.division,
            Curso: instancia.nombreCurso || "",
            Instancia: instancia.nombreInstancia || "",
          };
          ejes.forEach((eje) => {
            const m = (a?.materias || []).find((x) => (x.nombreMateria || x.materia) === eje);
            fila[eje] = m && String(m.nota ?? "").trim() ? m.nota : "Sin nota";
          });
          fila["Promedio Instancia"] = a?.tieneNotas ? formatearPromedio(a.promedioFinal) : "Sin nota";
          fila["Resultado"] = a?.tieneNotas ? a.resultadoFinal : "SIN NOTAS / REVISAR SI CORRESPONDE";
          fila["Motivo"] = a?.tieneNotas ? a.motivoResultado : "El alumno no registra notas en esta instancia; puede no corresponder por escalafón, recuperación u otra condición";
          fila["Tipo criterio"] = criterio.tipo;
          fila["Promedio mínimo"] = criterio.tipo === "EJES" ? "No aplica" : criterio.notaMinimaAprobacion;
          fila["Eje mínimo"] = criterio.tipo === "PROMEDIO" ? "No aplica" : criterio.notaMinimaEje;
          fila["Regular aprobado"] = criterio.regularCuentaComoAprobado ? "Sí" : "No";
          return fila;
        });
        agregarHoja(wb, `Inst ${instancia.id} ${instancia.nombreInstancia || ""}`, filas);
      });

      agregarHoja(
        wb,
        "Resumen Instancias",
        dataCurso.map((item) => {
          const instancia = item.instancia || {};
          const criterio = obtenerCriterioInstancia(instancia.id);
          const alumnos = normalizarAlumnosInstancia(item.data, instancia);
          const conNotas = alumnos.filter((a) => a.tieneNotas);
          const promedios = conNotas.map((a) => a.promedioFinal).filter((n) => n !== null);
          return {
            Curso: instancia.nombreCurso || "",
            Instancia: instancia.nombreInstancia || "",
            "Tipo criterio": criterio.tipo,
            "Promedio mínimo": criterio.tipo === "EJES" ? "No aplica" : criterio.notaMinimaAprobacion,
            "Eje mínimo": criterio.tipo === "PROMEDIO" ? "No aplica" : criterio.notaMinimaEje,
            "Regular aprobado": criterio.regularCuentaComoAprobado ? "Sí" : "No",
            "Total cursantes generales": ordenMeritoCurso.length,
            "Con notas": conNotas.length,
            "Sin notas / revisar": Math.max(ordenMeritoCurso.length - conNotas.length, 0),
            Aprobados: conNotas.filter((a) => a.resultadoFinal === "APROBADO").length,
            "No aprobados": conNotas.filter((a) => a.resultadoFinal === "NO APROBADO").length,
            "Promedio de cursantes con notas": promedios.length ? formatearPromedio(promedios.reduce((x, y) => x + y, 0) / promedios.length) : "Sin nota",
          };
        })
      );

      const nombreCurso =
        modoVista === "ESCUELA"
          ? escuelaActual || "escuela"
          : instanciaSeleccionada?.nombreCurso || instanciasCurso[0]?.nombreCurso || "curso";

      XLSX.writeFile(
        wb,
        `orden_merito_${modoVista === "ESCUELA" ? "escuela" : "curso"}_${limpiarNombreHoja(nombreCurso)}.xlsx`
      );
    } catch (error) {
      console.error("Error exportando curso completo:", error);
      alert("No se pudo exportar el curso completo");
    }
  };

  if (loading) {
    return <div style={styles.loading}>Cargando calificaciones...</div>;
  }

  if (!data) {
    return (
      <div style={styles.contenedor}>
        <button onClick={cargarDatos} style={styles.btnPrimario}>
          Cargar calificaciones
        </button>
      </div>
    );
  }

  const PanelCriterios = () => (
    <div style={styles.panelCriterios}>
      <div>
        <h3 style={{ margin: "0 0 6px" }}>Criterio de aprobación</h3>
        <p style={{ margin: 0, color: "#475569" }}>
          Aprueba con promedio final desde <b>{notaMinimaAprobacion}</b> y cada
          eje/materia debe tener mínimo <b>{notaMinimaEje}</b>. Regular{" "}
          <b>{regularCuentaComoAprobado ? "cuenta" : "no cuenta"}</b> como
          aprobado.
        </p>
      </div>

      <div style={styles.criteriosControls}>
        <select
          value={notaMinimaAprobacion}
          onChange={(e) => setNotaMinimaAprobacion(Number(e.target.value))}
          style={styles.select}
        >
          {[4, 5, 6, 7, 8].map((n) => (
            <option key={n} value={n}>
              Promedio desde {n}
            </option>
          ))}
        </select>

        <select
          value={notaMinimaEje}
          onChange={(e) => setNotaMinimaEje(Number(e.target.value))}
          style={styles.select}
        >
          {[1, 2, 3, 4, 5, 6].map((n) => (
            <option key={n} value={n}>
              Eje mínimo {n}
            </option>
          ))}
        </select>

        <label style={styles.checkRegular}>
          <input
            type="checkbox"
            checked={regularCuentaComoAprobado}
            onChange={(e) => setRegularCuentaComoAprobado(e.target.checked)}
          />
          Regular cuenta como aprobado
        </label>
      </div>
    </div>
  );

  const ConfiguracionInstancia = ({ instancia, compacta = false }) => {
    const criterio = obtenerCriterioInstancia(instancia.id);
    return (
      <div style={{ ...styles.configInstancia, marginTop: compacta ? 8 : 10 }}>
        <select value={criterio.tipo} onChange={(e) => actualizarCriterioInstancia(instancia.id, "tipo", e.target.value)} style={styles.select}>
          <option value="PROMEDIO">Solo promedio</option>
          <option value="EJES">Solo mínimo por eje</option>
          <option value="PROMEDIO_Y_EJES">Promedio + mínimo por eje</option>
        </select>
        {criterio.tipo !== "EJES" && (
          <select value={criterio.notaMinimaAprobacion} onChange={(e) => actualizarCriterioInstancia(instancia.id, "notaMinimaAprobacion", Number(e.target.value))} style={styles.select}>
            {[4,5,6,7,8,9,10].map((n) => <option key={n} value={n}>Promedio desde {n}</option>)}
          </select>
        )}
        {criterio.tipo !== "PROMEDIO" && (
          <select value={criterio.notaMinimaEje} onChange={(e) => actualizarCriterioInstancia(instancia.id, "notaMinimaEje", Number(e.target.value))} style={styles.select}>
            {[1,2,3,4,5,6,7,8,9,10].map((n) => <option key={n} value={n}>Eje mínimo {n}</option>)}
          </select>
        )}
        <label style={styles.checkRegular}>
          <input type="checkbox" checked={criterio.regularCuentaComoAprobado} onChange={(e) => actualizarCriterioInstancia(instancia.id, "regularCuentaComoAprobado", e.target.checked)} />
          Regular aprobado
        </label>
      </div>
    );
  };

const instanciasActivas = instanciasCurso.filter(
  (i) => String(i.estado || "").trim().toLowerCase() === "activo"
);

const cantidadSeleccionadasActivas = instanciasActivas.filter((i) =>
  instanciasSeleccionadas.includes(String(i.id))
).length;
  return (
    <div style={styles.contenedor}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.titulo}>
            {modoVista === "INSTANCIA"
              ? data.instancia?.nombreInstancia
              : modoVista === "ESCUELA"
              ? "Escuela completa"
              : "Curso completo"}
          </h2>

          <p style={styles.subtitulo}>{data.instancia?.nombreCurso}</p>
        </div>

        <div style={styles.botonesHeader}>
          <button
            onClick={() => setModoVista("INSTANCIA")}
            style={{
              ...styles.btnPrimario,
              background: modoVista === "INSTANCIA" ? "#2563eb" : "#64748b",
            }}
          >
            📊 Ver Instancia
          </button>

          <button
            onClick={() => setModoVista("CURSO")}
            style={{
              ...styles.btnPrimario,
              background: modoVista === "CURSO" ? "#059669" : "#64748b",
            }}
          >
            🏆 Ver Curso Completo
          </button>


          <button
            onClick={() => setModoVista("ESCUELA")}
            style={{
              ...styles.btnPrimario,
              background: modoVista === "ESCUELA" ? "#9333ea" : "#64748b",
            }}
          >
            🏫 Ver Escuela Completa
          </button>

          {modoVista === "INSTANCIA" ? (
            <button onClick={exportarExcel} style={styles.btnPrimario}>
              📘 Exportar Instancia
            </button>
          ) : (
            <button
              onClick={exportarCursoCompleto}
              style={{ ...styles.btnPrimario, background: "#7c3aed" }}
            >
              📗 Exportar Orden Mérito
            </button>
          )}
        </div>
      </div>

      <PanelCriterios />

      {modoVista === "CURSO" || modoVista === "ESCUELA" ? (
        <>
          {modoVista === "ESCUELA" && (
            <div style={styles.selectorInstancias}>
              <div style={styles.selectorHeader}>
                <div>
                  <h3 style={{ margin: 0 }}>Elegir cursos de {escuelaActual}</h3>
                  <p style={{ margin: "5px 0 0", color: "#64748b" }}>
                    Primero seleccioná uno o más cursos. Después se despliegan sus instancias activas para elegir cuáles entran al cálculo.
                  </p>
                </div>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button
                    onClick={seleccionarTodosCursosEscuela}
                    style={{ ...styles.btnPrimario, background: "#16a34a" }}
                  >
                    ✅ Mostrar todos los cursos
                  </button>

                  <button
                    onClick={limpiarCursosEscuela}
                    style={{ ...styles.btnPrimario, background: "#dc2626" }}
                  >
                    🧹 Limpiar
                  </button>

                  <button
                    onClick={cargarEscuelaCompleta}
                    style={{ ...styles.btnPrimario, background: "#0f766e" }}
                  >
                    🔄 Recalcular escuela
                  </button>
                </div>
              </div>

              <div style={styles.cursosEscuelaLista}>
                {cursosEscuela.map((curso) => {
                  const cursoId = String(curso.id);
                  const checked = cursosEscuelaSeleccionados.includes(cursoId);
                  const instanciasDelCurso = instanciasEscuelaPorCurso.get(cursoId) || [];
                  const seleccionadasDelCurso = instanciasDelCurso.filter((i) =>
                    instanciasEscuelaSeleccionadas.includes(String(i.id))
                  ).length;

                  return (
                    <div
                      key={curso.id}
                      style={{
                        ...styles.cursoEscuelaCard,
                        border: checked
                          ? "2px solid #9333ea"
                          : "1px solid #cbd5e1",
                        background: checked ? "#f5f3ff" : "white",
                      }}
                    >
                      <label style={styles.cursoEscuelaHeader}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleCursoEscuela(curso.id)}
                          style={{ transform: "scale(1.2)" }}
                        />

                        <div style={{ flex: 1 }}>
                          <b>{curso.nombreCurso}</b>
                          <br />
                          <small>
                            {curso.sede || "Sin sede"} | Instancias activas: {instanciasDelCurso.length}
                          </small>
                        </div>

                        <span style={styles.badgeCantidad}>
                          {seleccionadasDelCurso}/{instanciasDelCurso.length}
                        </span>
                      </label>

                      {checked && (
                        <div style={styles.instanciasCursoEscuelaBox}>
                          <div style={styles.instanciasCursoEscuelaAcciones}>
                            <button
                              type="button"
                              onClick={() => seleccionarTodasInstanciasCursoEscuela(curso.id)}
                              style={{ ...styles.btnMini, background: "#16a34a" }}
                            >
                              Todas las instancias
                            </button>

                            <button
                              type="button"
                              onClick={() => limpiarInstanciasCursoEscuela(curso.id)}
                              style={{ ...styles.btnMini, background: "#dc2626" }}
                            >
                              Limpiar instancias
                            </button>
                          </div>

                          {instanciasDelCurso.length === 0 ? (
                            <p style={styles.selectorInfo}>Este curso no tiene instancias activas.</p>
                          ) : (
                            <div style={styles.instanciasGridInterna}>
                              {instanciasDelCurso.map((instancia) => {
                                const instanciaChecked = instanciasEscuelaSeleccionadas.includes(String(instancia.id));

                                return (
                                  <label
                                    key={instancia.id}
                                    style={{
                                      ...styles.instanciaMiniCard,
                                      border: instanciaChecked
                                        ? "2px solid #7c3aed"
                                        : "1px solid #cbd5e1",
                                      background: instanciaChecked ? "#ede9fe" : "white",
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={instanciaChecked}
                                      onChange={() => toggleInstanciaEscuela(instancia.id)}
                                    />

                                    <div>
                                      <b>{instancia.nombreInstancia}</b>
                                      <br />
                                      <small>{instancia.estado}</small>
                                      {instanciaChecked && <ConfiguracionInstancia instancia={instancia} compacta />}
                                    </div>
                                  </label>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <p style={styles.selectorInfo}>
                Cursos seleccionados: <b>{cursosEscuelaSeleccionados.length}</b> de <b>{cursosEscuela.length}</b> | Instancias seleccionadas: <b>{cantidadInstanciasEscuelaSeleccionadasActivas}</b> de <b>{instanciasEscuelaActivas.length}</b>
              </p>
            </div>
          )}

          {modoVista === "CURSO" && (
          <div style={styles.selectorInstancias}>
            <div style={styles.selectorHeader}>
              <h3 style={{ margin: 0 }}>Elegir instancias para calcular</h3>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  onClick={seleccionarTodasInstancias}
                  style={{ ...styles.btnPrimario, background: "#16a34a" }}
                >
                  ✅ Todas
                </button>

                <button
                  onClick={limpiarInstanciasSeleccionadas}
                  style={{ ...styles.btnPrimario, background: "#dc2626" }}
                >
                  🧹 Limpiar
                </button>

                <button
                  onClick={modoVista === "ESCUELA" ? cargarEscuelaCompleta : cargarCursoCompleto}
                  style={{ ...styles.btnPrimario, background: "#0f766e" }}
                >
                  🔄 Recalcular
                </button>
              </div>
            </div>

            <div style={styles.instanciasGrid}>
              {instanciasCurso
  .filter(
    (instancia) =>
      String(instancia.estado || "").trim().toLowerCase() === "activo"
  )
  .map((instancia) => {
                const checked = instanciasSeleccionadas.includes(
                  String(instancia.id)
                );

                return (
                  <label
                    key={instancia.id}
                    style={{
                      ...styles.instanciaCheckCard,
                      border: checked
                        ? "2px solid #16a34a"
                        : "1px solid #cbd5e1",
                      background: checked ? "#ecfdf5" : "white",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleInstanciaCurso(instancia.id)}
                      style={{ transform: "scale(1.2)" }}
                    />

                    <div>
                      <b>{instancia.nombreInstancia}</b>
                      <br />
                      <small>{instancia.estado}</small>
                      {checked && <ConfiguracionInstancia instancia={instancia} compacta />}
                    </div>
                  </label>
                );
              })}
            </div>

            <p style={styles.selectorInfo}>
             Instancias seleccionadas : <b>{cantidadSeleccionadasActivas}</b> de{" "}
<b>{instanciasActivas.length}</b>
            </p>
          </div>
          )}

          {loadingCurso ? (
            <div style={styles.loading}>{modoVista === "ESCUELA" ? "Cargando escuela completa..." : "Cargando curso completo..."}</div>
          ) : (
            <>
              <div style={styles.cards}>
                <Card
                  titulo="Instancias"
                  valor={resumenCurso.instancias}
                  detalle="Instancias usadas"
                  color="#2563eb"
                />
                <Card
                  titulo="Alumnos"
                  valor={resumenCurso.totalAlumnos}
                  detalle="Alumnos únicos"
                />
                <Card
                  titulo="Aprobados"
                  valor={resumenCurso.aprobados}
                  detalle="Sin pendientes"
                  color="#16a34a"
                />
                <Card
                  titulo="A revisar"
                  valor={resumenCurso.revisar}
                  detalle="Con pendientes o no aprobados"
                  color="#dc2626"
                />
                <Card
                  titulo="Promedio"
                  valor={formatearPromedio(resumenCurso.promedio)}
                  detalle="Promedio general"
                  color="#7c3aed"
                />
              </div>

              <div style={styles.panelSorpresa}>
                <h3 style={styles.panelTitulo}>{modoVista === "ESCUELA" ? "Lectura de la escuela completa" : "Lectura del curso completo"}</h3>
                <p style={styles.panelTexto}>
                  Calculado sobre <b>{resumenCurso.instancias}</b> instancia/s.
                  Hay <b>{resumenCurso.totalAlumnos}</b> alumnos únicos, con
                  promedio general de{" "}
                  <b>{formatearPromedio(resumenCurso.promedio)}</b>. Alumnos
                  para revisar: <b>{resumenCurso.revisar}</b>.
                </p>
              </div>

              <div style={styles.filtros}>
                <input
                  placeholder="Buscar por DNI, apellido o nombre..."
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  style={styles.input}
                />

                <select
                  value={division}
                  onChange={(e) => setDivision(e.target.value)}
                  style={styles.select}
                >
                  <option value="TODAS">Todas las divisiones</option>
                  {divisionesCurso.map((d) => (
                    <option key={d} value={d}>
                      División {d}
                    </option>
                  ))}
                </select>

                <select
                  value={estado}
                  onChange={(e) => setEstado(e.target.value)}
                  style={styles.select}
                >
                  <option value="TODOS">Todos los estados</option>
                  <option value="APROBADO">Aprobados</option>
                  <option value="NO APROBADO">A revisar</option>
                </select>

                <button
                  onClick={cargarCursoCompleto}
                  style={{ ...styles.btnPrimario, background: "#0f766e" }}
                >
                  🔄 Recalcular
                </button>
              </div>

              <h3 style={styles.seccionTitulo}>🏆 Orden de mérito</h3>

              <div style={styles.tablaWrapper}>
                <table style={styles.tabla}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Orden</th>
                      <th style={styles.th}>DNI</th>
                      <th style={styles.th}>Alumno</th>
                      <th style={styles.th}>Div.</th>
                      <th style={styles.th}>Estado</th>
                      <th style={styles.th}>Instancias</th>
                      <th style={styles.th}>Ejes</th>
                      <th style={styles.th}>Pend.</th>
                      <th style={styles.th}>Promedio</th>
                      <th style={styles.th}>Avance</th>
                      <th style={styles.th}>Detalle</th>
                    </tr>
                  </thead>

                  <tbody>
                    {ordenMeritoFiltrado.map((alumno) => {
                      const detalleAbierto =
                        String(detalleAlumnoAbierto) === String(alumno.dni);

                      return (
                        <React.Fragment key={alumno.dni}>
                          <tr
                            style={{
                              background:
                                alumno.ordenMerito <= 3
                                  ? "#fefce8"
                                  : alumno.estadoGeneral !== "APROBADO"
                                  ? "#fef2f2"
                                  : "white",
                            }}
                          >
                            <td style={styles.tdCenter}>
                              <b>#{alumno.ordenMerito}</b>
                            </td>
                            <td style={styles.td}>{alumno.dni}</td>
                            <td style={styles.td}>
                              <b>{alumno.nombreCompleto}</b>
                            </td>
                            <td style={styles.tdCenter}>{alumno.division}</td>
                            <td style={styles.tdCenter}>
                              <span
                                style={{
                                  ...styles.badge,
                                  background:
                                    alumno.estadoGeneral === "APROBADO"
                                      ? "#dcfce7"
                                      : "#fee2e2",
                                  color:
                                    alumno.estadoGeneral === "APROBADO"
                                      ? "#166534"
                                      : "#991b1b",
                                }}
                              >
                                {alumno.estadoGeneral}
                              </span>
                            </td>
                            <td style={styles.tdCenter}>
                              {alumno.cantidadInstancias}
                            </td>
                            <td style={styles.tdCenter}>
                              {alumno.aprobadas}/{alumno.totalMaterias}
                            </td>
                            <td style={styles.tdCenter}>{alumno.pendientes}</td>
                            <td style={styles.tdCenter}>
                              <b>{formatearPromedio(alumno.promedioFinal)}</b>
                            </td>
                            <td style={styles.td}>
                              <Barra porcentaje={alumno.porcentaje} />
                            </td>
                            <td style={styles.td}>
                              <button
                                type="button"
                                onClick={() =>
                                  setDetalleAlumnoAbierto((actual) =>
                                    String(actual) === String(alumno.dni)
                                      ? null
                                      : alumno.dni
                                  )
                                }
                                style={styles.btnDetalle}
                              >
                                {detalleAbierto
                                  ? "▼ Ocultar detalle"
                                  : "▶ Ver detalle"}
                              </button>
                            </td>
                          </tr>

                          {detalleAbierto && (
                            <tr>
                              <td colSpan={11} style={styles.tdDetalleExpandido}>
                                <div style={styles.detalleAlumnoPanel}>
                                  <h4 style={styles.detalleAlumnoTitulo}>
                                    Detalle del alumno
                                  </h4>

                                  <div style={styles.detalleResumenGrid}>
                                    <div>
                                      <b>👤 DNI:</b> {alumno.dni}
                                    </div>
                                    <div>
                                      <b>Alumno:</b> {alumno.nombreCompleto}
                                    </div>
                                    <div>
                                      <b>División:</b> {alumno.division || "Sin división"}
                                    </div>
                                    <div>
                                      <b>Estado general:</b>{" "}
                                      <span style={styles.badge}>
                                        {alumno.estadoGeneral}
                                      </span>
                                    </div>
                                    <div>
                                      <b>Instancias con notas:</b>{" "}
                                      {alumno.instanciasConNotas} de{" "}
                                      {alumno.cantidadInstancias}
                                    </div>
                                    <div>
                                      <b>Promedio general:</b>{" "}
                                      {formatearPromedio(alumno.promedioFinal)}
                                    </div>
                                    <div>
                                      <b>Ejes aprobados:</b>{" "}
                                      {alumno.aprobadas}/{alumno.totalMaterias}
                                    </div>
                                    <div>
                                      <b>Pendientes:</b> {alumno.pendientes}
                                    </div>
                                    <div>
                                      <b>Avance:</b> {alumno.porcentaje}%
                                    </div>
                                  </div>

                                  <div style={styles.instanciasDetalleGrid}>
                                    {(alumno.instanciasDetalle || []).map(
                                      (instanciaDetalle, idx) => {
                                        const criterio =
                                          instanciaDetalle.criterio ||
                                          criterioBase;
                                        const aprobada =
                                          instanciaDetalle.resultado ===
                                          "APROBADO";

                                        return (
                                          <div
                                            key={`${instanciaDetalle.instanciaId}-${idx}`}
                                            style={{
                                              ...styles.instanciaDetalleCard,
                                              borderColor: instanciaDetalle.tieneNotas
                                                ? aprobada
                                                  ? "#22c55e"
                                                  : "#ef4444"
                                                : "#f59e0b",
                                              background: instanciaDetalle.tieneNotas
                                                ? aprobada
                                                  ? "#f0fdf4"
                                                  : "#fef2f2"
                                                : "#fffbeb",
                                            }}
                                          >
                                            <div style={styles.instanciaDetalleEncabezado}>
                                              <div>
                                                {instanciaDetalle.nombreCurso && (
                                                  <div style={styles.nombreCursoDetalle}>
                                                    {instanciaDetalle.nombreCurso}
                                                  </div>
                                                )}
                                                <h5 style={styles.nombreInstanciaDetalle}>
                                                  {instanciaDetalle.nombreInstancia}
                                                </h5>
                                              </div>

                                              <span
                                                style={{
                                                  ...styles.badge,
                                                  background: instanciaDetalle.tieneNotas
                                                    ? aprobada
                                                      ? "#dcfce7"
                                                      : "#fee2e2"
                                                    : "#fef3c7",
                                                  color: instanciaDetalle.tieneNotas
                                                    ? aprobada
                                                      ? "#166534"
                                                      : "#991b1b"
                                                    : "#92400e",
                                                }}
                                              >
                                                {instanciaDetalle.tieneNotas
                                                  ? instanciaDetalle.resultado
                                                  : "SIN NOTAS"}
                                              </span>
                                            </div>

                                            <div style={styles.criterioDetalle}>
                                              <div>
                                                <b>Criterio:</b>{" "}
                                                {criterio.tipo === "PROMEDIO"
                                                  ? "Solo promedio"
                                                  : criterio.tipo === "EJES"
                                                  ? "Solo mínimo por eje"
                                                  : "Promedio + mínimo por eje"}
                                              </div>
                                              <div>
                                                <b>Promedio mínimo:</b>{" "}
                                                {criterio.tipo === "EJES"
                                                  ? "No aplica"
                                                  : criterio.notaMinimaAprobacion}
                                              </div>
                                              <div>
                                                <b>Nota mínima por eje:</b>{" "}
                                                {criterio.tipo === "PROMEDIO"
                                                  ? "No aplica"
                                                  : criterio.notaMinimaEje}
                                              </div>
                                              <div>
                                                <b>Regular aprobado:</b>{" "}
                                                {criterio.regularCuentaComoAprobado
                                                  ? "Sí"
                                                  : "No"}
                                              </div>
                                            </div>

                                            <div style={styles.resultadoInstanciaDetalle}>
                                              <div>
                                                <b>Promedio instancia:</b>{" "}
                                                {instanciaDetalle.tieneNotas &&
                                                instanciaDetalle.promedio !== null
                                                  ? formatearPromedio(
                                                      instanciaDetalle.promedio
                                                    )
                                                  : "Sin nota"}
                                              </div>
                                              <div>
                                                <b>Resultado:</b>{" "}
                                                {instanciaDetalle.tieneNotas
                                                  ? instanciaDetalle.resultado
                                                  : "Revisar si corresponde"}
                                              </div>
                                            </div>

                                            <div style={styles.ejesDetalleLista}>
                                              {(instanciaDetalle.materias || []).map(
                                                (materiaInstancia, materiaIdx) => {
                                                  const aprobadaMateria =
                                                    esEjeAprobadoConCriterio(
                                                      materiaInstancia.nota,
                                                      criterio
                                                    );

                                                  return (
                                                    <div
                                                      key={`${materiaInstancia.nombreMateria}-${materiaIdx}`}
                                                      style={{
                                                        ...styles.ejeDetalleItem,
                                                        borderLeftColor:
                                                          aprobadaMateria
                                                            ? "#22c55e"
                                                            : "#ef4444",
                                                      }}
                                                    >
                                                      <div>
                                                        <b>
                                                          {materiaInstancia.nombreMateria ||
                                                            materiaInstancia.materia ||
                                                            "Sin materia"}
                                                        </b>
                                                      </div>
                                                      <div>
                                                        Nota:{" "}
                                                        <b>
                                                          {materiaInstancia.nota ===
                                                            null ||
                                                          materiaInstancia.nota ===
                                                            undefined ||
                                                          String(
                                                            materiaInstancia.nota
                                                          ).trim() === ""
                                                            ? "Sin nota"
                                                            : materiaInstancia.nota}
                                                        </b>
                                                      </div>
                                                      <small>
                                                        {materiaInstancia.califico ||
                                                          "Sin calificador"}
                                                      </small>
                                                    </div>
                                                  );
                                                }
                                              )}

                                              {(!instanciaDetalle.materias ||
                                                instanciaDetalle.materias.length ===
                                                  0) && (
                                                <div style={styles.sinNotasDetalle}>
                                                  El alumno no registra notas en esta
                                                  instancia. Revisar si corresponde por
                                                  escalafón, recuperatorio u otra
                                                  condición.
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        );
                                      }
                                    )}
                                  </div>

                                  {alumno.advertencia &&
                                    alumno.advertencia !== "Completo" && (
                                      <div style={styles.advertenciaDetalle}>
                                        ⚠️ <b>Observación:</b>{" "}
                                        {alumno.advertencia}
                                      </div>
                                    )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}

                    {ordenMeritoFiltrado.length === 0 && (
                      <tr>
                        <td style={styles.tdCenter} colSpan={11}>
                          No hay alumnos para mostrar.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      ) : (
        <>
          <div style={styles.cards}>
            <Card
              titulo="Total alumnos"
              valor={total}
              detalle="Evaluados en la instancia"
            />
            <Card
              titulo="Aprobados"
              valor={cantidadAprobadosCalculados}
              detalle={`${porcentajeAprobados}% del total`}
              color="#16a34a"
            />
            <Card
              titulo="No aprobados"
              valor={cantidadNoAprobadosCalculados}
              detalle={`${100 - porcentajeAprobados}% del total`}
              color="#dc2626"
            />
            <Card
              titulo="Materias/Ejes"
              valor={materias.length}
              detalle="Ejes calificados"
              color="#2563eb"
            />
          </div>

          <div style={styles.panelSorpresa}>
            <h3 style={styles.panelTitulo}>Lectura rápida</h3>
            <p style={styles.panelTexto}>
              La instancia tiene un rendimiento general de{" "}
              <b>{porcentajeAprobados}%</b>. Hay <b>{alumnosEnRiesgo.length}</b>{" "}
              alumnos que conviene revisar.
            </p>
          </div>

          <div style={styles.filtros}>
            <input
              placeholder="Buscar por DNI, apellido o nombre..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              style={styles.input}
            />

            <select
              value={division}
              onChange={(e) => setDivision(e.target.value)}
              style={styles.select}
            >
              <option value="TODAS">Todas las divisiones</option>
              {divisiones.map((d) => (
                <option key={d} value={d}>
                  División {d}
                </option>
              ))}
            </select>

            <select
              value={materia}
              onChange={(e) => setMateria(e.target.value)}
              style={styles.select}
            >
              <option value="TODAS">Todas las materias/ejes</option>
              {materias.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>

            <select
              value={estado}
              onChange={(e) => setEstado(e.target.value)}
              style={styles.select}
            >
              <option value="TODOS">Todos los estados</option>
              <option value="APROBADO">Aprobados</option>
              <option value="NO APROBADO">No aprobados</option>
              <option value="SIN DEFINIR">Sin definir</option>
            </select>
          </div>

          <h3 style={styles.seccionTitulo}>Resumen por división</h3>

          <div style={styles.tablaWrapper}>
            <table style={styles.tabla}>
              <thead>
                <tr>
                  <th style={styles.th}>División</th>
                  <th style={styles.th}>Total</th>
                  <th style={styles.th}>Aprobados</th>
                  <th style={styles.th}>No aprobados</th>
                  <th style={styles.th}>Rendimiento</th>
                </tr>
              </thead>

              <tbody>
                {resumenDivisiones.map((d) => (
                  <tr key={d.division}>
                    <td style={styles.td}>
                      <b>División {d.division}</b>
                    </td>
                    <td style={styles.tdCenter}>{d.total}</td>
                    <td style={styles.tdCenter}>{d.aprobados}</td>
                    <td style={styles.tdCenter}>{d.noAprobados}</td>
                    <td style={styles.td}>
                      <Barra porcentaje={d.porcentaje} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3 style={styles.seccionTitulo}>Resumen por materia/eje</h3>

          <div style={styles.tablaWrapper}>
            <table style={styles.tabla}>
              <thead>
                <tr>
                  <th style={styles.th}>Materia/Eje</th>
                  <th style={styles.th}>Total</th>
                  <th style={styles.th}>Aprobados</th>
                  <th style={styles.th}>No aprobados</th>
                  <th style={styles.th}>Regulares</th>
                  <th style={styles.th}>Libres</th>
                  <th style={styles.th}>Rendimiento</th>
                  <th style={styles.th}>Docentes / calificadores</th>
                </tr>
              </thead>

              <tbody>
                {resumenMaterias.map((m) => (
                  <tr key={m.materia}>
                    <td style={styles.td}>
                      <b>{m.materia}</b>
                    </td>
                    <td style={styles.tdCenter}>{m.total}</td>
                    <td style={styles.tdCenter}>{m.aprobados}</td>
                    <td style={styles.tdCenter}>{m.noAprobados}</td>
                    <td style={styles.tdCenter}>{m.regulares}</td>
                    <td style={styles.tdCenter}>{m.libres}</td>
                    <td style={styles.td}>
                      <Barra porcentaje={m.porcentaje} />
                    </td>
                    <td style={styles.tdSmall}>
                      {m.docentes.slice(0, 2).join(" | ")}
                      {m.docentes.length > 2
                        ? ` +${m.docentes.length - 2}`
                        : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3 style={styles.seccionTitulo}>Alumnos</h3>

          <div style={styles.tablaWrapper}>
            <table style={styles.tabla}>
              <thead>
                <tr>
                  <th style={styles.th}>DNI</th>
                  <th style={styles.th}>Alumno</th>
                  <th style={styles.th}>Div.</th>
                  <th style={styles.th}>Resultado</th>
                  <th style={styles.th}>Promedio</th>
                  <th style={styles.th}>Ejes</th>
                  <th style={styles.th}>Avance</th>
                  <th style={styles.th}>Detalle</th>
                </tr>
              </thead>

              <tbody>
                {alumnosFiltrados.map((alumno) => (
                  <tr key={alumno.dni}>
                    <td style={styles.td}>{alumno.dni}</td>
                    <td style={styles.td}>
                      <b>{alumno.nombreCompleto}</b>
                      <br />
                      <small>{alumno.motivoResultado}</small>
                    </td>
                    <td style={styles.tdCenter}>{alumno.division}</td>
                    <td style={styles.tdCenter}>
                      <span
                        style={{
                          ...styles.badge,
                          background:
                            alumno.resultadoFinal === "APROBADO"
                              ? "#dcfce7"
                              : "#fee2e2",
                          color:
                            alumno.resultadoFinal === "APROBADO"
                              ? "#166534"
                              : "#991b1b",
                        }}
                      >
                        {alumno.resultadoFinal}
                      </span>
                    </td>
                    <td style={styles.tdCenter}>
                      <b>{formatearPromedio(alumno.promedioFinal)}</b>
                    </td>
                    <td style={styles.tdCenter}>
                      {alumno.aprobadas}/{alumno.totalMaterias}
                    </td>
                    <td style={styles.td}>
                      <Barra porcentaje={alumno.porcentaje} />
                    </td>
                    <td style={styles.td}>
                      <details>
                        <summary style={styles.summary}>Ver materias</summary>

                        <div style={styles.materiasBoxHorizontal}>
                          {alumno.materias.map((m, idx) => (
                            <div
                              key={idx}
                              style={{
                                ...styles.materiaItem,
                                background: esMateriaAprobada(m.nota)
                                  ? "#ecfdf5"
                                  : "#fef2f2",
                                border: esMateriaAprobada(m.nota)
                                  ? "2px solid #22c55e"
                                  : "2px solid #ef4444",
                              }}
                            >
                              <b>{m.nombreMateria}</b>
                              <br />
                              <span style={{ fontWeight: "bold" }}>
                                Nota: {m.nota || "Sin nota"}
                              </span>
                              <br />
                              <small>{obtenerEstadoNota(m.nota)}</small>
                              <br />
                              <small>{m.califico || "Sin calificador"}</small>
                            </div>
                          ))}
                        </div>
                      </details>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3 style={styles.seccionTitulo}>Alumnos para revisar</h3>

          <div style={styles.riesgoGrid}>
            {alumnosEnRiesgo.slice(0, 12).map((a) => (
              <div key={a.dni} style={styles.riesgoCard}>
                <b>{a.nombreCompleto}</b>
                <p>DNI: {a.dni}</p>
                <p>División: {a.division}</p>
                <p>Resultado: {a.resultadoFinal}</p>
                <p>Motivo: {a.motivoResultado}</p>
                <p>Promedio: {formatearPromedio(a.promedioFinal)}</p>
                <p>Pendientes: {a.pendientes}</p>
                <p>
                  Materias/ejes a recuperar:{" "}
                  <b>
                    {materiasARecuperarTexto(a.materias) || "Sin pendientes"}
                  </b>
                </p>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const Card = ({ titulo, valor, detalle, color = "#111827" }) => (
  <div style={styles.card}>
    <p style={styles.cardTitulo}>{titulo}</p>
    <h2 style={{ ...styles.cardValor, color }}>{valor}</h2>
    <p style={styles.cardDetalle}>{detalle}</p>
  </div>
);

const Barra = ({ porcentaje }) => (
  <div style={styles.barraFondo}>
    <div
      style={{
        ...styles.barra,
        width: `${porcentaje}%`,
        background:
          porcentaje >= 80
            ? "#16a34a"
            : porcentaje >= 50
            ? "#f59e0b"
            : "#dc2626",
      }}
    />
    <span style={styles.barraTexto}>{porcentaje}%</span>
  </div>
);

const styles = {
  contenedor: {
    padding: 20,
    fontFamily: "Arial, sans-serif",
    background: "#f8fafc",
    minHeight: "100vh",
  },
  loading: {
    padding: 30,
    fontSize: 18,
    textAlign: "center",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    gap: 15,
    alignItems: "center",
    marginBottom: 20,
    flexWrap: "wrap",
  },
  botonesHeader: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },
  titulo: {
    margin: 0,
    fontSize: 28,
    color: "#0f172a",
  },
  subtitulo: {
    margin: "6px 0 0",
    color: "#64748b",
  },
  btnPrimario: {
    border: "none",
    background: "#2563eb",
    color: "white",
    padding: "10px 16px",
    borderRadius: 10,
    cursor: "pointer",
    fontWeight: "bold",
  },
  panelCriterios: {
    background: "white",
    borderRadius: 16,
    padding: 16,
    boxShadow: "0 8px 22px rgba(15,23,42,0.08)",
    marginBottom: 18,
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "center",
    flexWrap: "wrap",
    borderLeft: "6px solid #7c3aed",
  },
  criteriosControls: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },
  checkRegular: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontWeight: "bold",
    color: "#334155",
  },
  cards: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 15,
    marginBottom: 18,
  },
  card: {
    background: "white",
    borderRadius: 16,
    padding: 18,
    boxShadow: "0 8px 22px rgba(15,23,42,0.08)",
  },
  cardTitulo: {
    margin: 0,
    color: "#64748b",
    fontSize: 14,
  },
  cardValor: {
    margin: "8px 0",
    fontSize: 34,
  },
  cardDetalle: {
    margin: 0,
    color: "#64748b",
    fontSize: 13,
  },
  panelSorpresa: {
    background: "linear-gradient(135deg, #eef2ff, #ecfeff)",
    border: "1px solid #c7d2fe",
    borderRadius: 16,
    padding: 18,
    marginBottom: 18,
  },
  panelTitulo: {
    margin: "0 0 6px",
    color: "#1e293b",
  },
  panelTexto: {
    margin: 0,
    color: "#334155",
  },
  filtros: {
    display: "grid",
    gridTemplateColumns: "2fr 1fr 1fr 1fr",
    gap: 10,
    marginBottom: 20,
  },
  input: {
    padding: 11,
    border: "1px solid #cbd5e1",
    borderRadius: 10,
  },
  select: {
    padding: 11,
    border: "1px solid #cbd5e1",
    borderRadius: 10,
    background: "white",
  },
  seccionTitulo: {
    margin: "22px 0 10px",
    color: "#0f172a",
  },
  tablaWrapper: {
    overflowX: "auto",
    background: "white",
    borderRadius: 16,
    boxShadow: "0 8px 22px rgba(15,23,42,0.08)",
    marginBottom: 20,
  },
  tabla: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: 1000,
  },
  th: {
    background: "#0f172a",
    color: "white",
    padding: 12,
    textAlign: "left",
    fontSize: 13,
  },
  td: {
    padding: 11,
    borderBottom: "1px solid #e2e8f0",
    color: "#1e293b",
    verticalAlign: "top",
  },
  tdCenter: {
    padding: 11,
    borderBottom: "1px solid #e2e8f0",
    color: "#1e293b",
    textAlign: "center",
    verticalAlign: "top",
  },
  tdSmall: {
    padding: 11,
    borderBottom: "1px solid #e2e8f0",
    color: "#475569",
    fontSize: 12,
  },
  badge: {
    display: "inline-block",
    padding: "5px 9px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: "bold",
  },
  barraFondo: {
    position: "relative",
    height: 22,
    background: "#e2e8f0",
    borderRadius: 999,
    overflow: "hidden",
    minWidth: 140,
  },
  barra: {
    height: "100%",
    borderRadius: 999,
  },
  barraTexto: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: "bold",
    color: "#0f172a",
  },
  summary: {
    cursor: "pointer",
    fontWeight: "bold",
    color: "#2563eb",
  },
  riesgoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 12,
  },
  riesgoCard: {
    background: "white",
    borderRadius: 14,
    padding: 14,
    borderLeft: "5px solid #dc2626",
    boxShadow: "0 8px 22px rgba(15,23,42,0.08)",
  },
  materiasBoxHorizontal: {
    marginTop: 8,
    display: "flex",
    flexDirection: "row",
    gap: 8,
    flexWrap: "nowrap",
    overflowX: "auto",
    maxWidth: "100%",
    paddingBottom: 6,
  },
  materiaItem: {
    minWidth: 230,
    maxWidth: 260,
    padding: 10,
    borderRadius: 10,
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    fontSize: 12,
    flex: "0 0 auto",
  },
  btnDetalle: {
    border: "none",
    background: "transparent",
    color: "#2563eb",
    fontWeight: "bold",
    cursor: "pointer",
    padding: "6px 0",
    whiteSpace: "nowrap",
  },
  tdDetalleExpandido: {
    padding: 0,
    borderBottom: "1px solid #cbd5e1",
    background: "#f8fafc",
  },
  detalleAlumnoPanel: {
    margin: 14,
    padding: 16,
    background: "white",
    border: "1px solid #cbd5e1",
    borderRadius: 14,
    boxShadow: "0 8px 20px rgba(15,23,42,0.06)",
  },
  detalleAlumnoTitulo: {
    margin: "0 0 12px",
    color: "#0f172a",
    fontSize: 18,
  },
  detalleResumenGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
    gap: 10,
    padding: 12,
    marginBottom: 14,
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    background: "#f8fafc",
    color: "#334155",
    fontSize: 13,
  },
  instanciasDetalleGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: 12,
    width: "100%",
  },
  instanciaDetalleCard: {
    minWidth: 0,
    padding: 14,
    border: "2px solid",
    borderRadius: 12,
    color: "#1e293b",
  },
  instanciaDetalleEncabezado: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 10,
    marginBottom: 10,
  },
  nombreCursoDetalle: {
    fontWeight: "bold",
    fontSize: 13,
    color: "#334155",
    textTransform: "uppercase",
  },
  nombreInstanciaDetalle: {
    margin: "3px 0 0",
    fontSize: 15,
    color: "#0f172a",
  },
  criterioDetalle: {
    display: "grid",
    gap: 5,
    padding: "9px 0",
    borderTop: "1px solid rgba(148,163,184,0.45)",
    borderBottom: "1px solid rgba(148,163,184,0.45)",
    fontSize: 12,
  },
  resultadoInstanciaDetalle: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: 7,
    padding: "10px 0",
    color: "#1d4ed8",
    fontSize: 13,
  },
  ejesDetalleLista: {
    display: "grid",
    gap: 7,
    marginTop: 4,
  },
  ejeDetalleItem: {
    padding: "8px 10px",
    background: "rgba(255,255,255,0.78)",
    border: "1px solid #e2e8f0",
    borderLeft: "4px solid",
    borderRadius: 8,
    fontSize: 12,
    overflowWrap: "anywhere",
  },
  sinNotasDetalle: {
    padding: 10,
    borderRadius: 8,
    background: "#fef3c7",
    color: "#92400e",
    fontSize: 12,
  },
  advertenciaDetalle: {
    marginTop: 14,
    padding: 12,
    borderRadius: 10,
    border: "1px solid #fbbf24",
    background: "#fffbeb",
    color: "#92400e",
    fontSize: 13,
  },
  selectorInstancias: {
    background: "white",
    borderRadius: 16,
    padding: 16,
    boxShadow: "0 8px 22px rgba(15,23,42,0.08)",
    marginBottom: 18,
  },
  selectorHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 12,
  },
  instanciasGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 10,
  },
  instanciaCheckCard: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: 12,
    borderRadius: 12,
    cursor: "pointer",
  },
  cursosEscuelaLista: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 12,
  },
  cursoEscuelaCard: {
    borderRadius: 14,
    padding: 12,
  },
  cursoEscuelaHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    cursor: "pointer",
  },
  badgeCantidad: {
    background: "#ede9fe",
    color: "#5b21b6",
    borderRadius: 999,
    padding: "5px 10px",
    fontWeight: "bold",
    fontSize: 12,
  },
  instanciasCursoEscuelaBox: {
    marginTop: 12,
    paddingTop: 12,
    borderTop: "1px solid #ddd6fe",
  },
  instanciasCursoEscuelaAcciones: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    marginBottom: 10,
  },
  instanciasGridInterna: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 8,
  },
  instanciaMiniCard: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    padding: 10,
    borderRadius: 10,
    cursor: "pointer",
  },
  btnMini: {
    border: "none",
    color: "white",
    padding: "7px 10px",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: "bold",
    fontSize: 12,
  },
  configInstancia: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    alignItems: "center",
    padding: 10,
    borderRadius: 10,
    background: "#f8fafc",
    border: "1px dashed #94a3b8",
  },
  selectorInfo: {
    margin: "12px 0 0",
    color: "#475569",
  },
};

export default TableroCalificacionesInstancia;